import heroImage from "@/assets/hero-bg.jpg";
import mobilityImage from "@/assets/program-mobility.jpg";
import strengthImage from "@/assets/program-strength-range.jpg";
import resilienceImage from "@/assets/program-resilience.jpg";
import dietImage from "@/assets/diet-program.jpg";

export const SITE_URL = "https://ptmetinozdemir.com";
export const SITE_NAME = "PT Metin Ozdemir";

export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/nMR1jvF1cFVNvG3xwA5OnyA6Vw03/social-images/social-1773868877975-WEb_Site_Hero.webp";

export const SUPPORTED_LANGS = ["tr", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
export const DEFAULT_LANG: Lang = "tr";

/**
 * Her dil için yayınlanan hreflang kodları. Dil-ülke kodları (tr-TR, en-US...)
 * Google'ın doğru bölge/dil sürümünü eşleştirmesini sağlar; salt dil kodu
 * (tr, en) diğer bölgeler için fallback görevi görür.
 */
export const HREFLANG_CODES: Record<Lang, string[]> = {
  tr: ["tr", "tr-TR"],
  en: ["en", "en-US", "en-GB"],
};

/** Tüm hreflang kodlarının, ait oldukları dil varyantı ile düz listesi. */
export const HREFLANG_ENTRIES: { code: string; lang: Lang }[] =
  SUPPORTED_LANGS.flatMap((lang) =>
    HREFLANG_CODES[lang].map((code) => ({ code, lang })),
  );


export const normalizeLang = (value?: string): Lang => {
  const code = value?.trim().toLowerCase().split("-")[0];
  return (SUPPORTED_LANGS as readonly string[]).includes(code ?? "")
    ? (code as Lang)
    : DEFAULT_LANG;
};

/** Social crawlers need absolute https URLs; bundler paths are root-relative. */
export const absoluteUrl = (value: string) =>
  /^https?:\/\//i.test(value) ? value : `${SITE_URL}${value.startsWith("/") ? "" : "/"}${value}`;

export interface PageMeta {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

/** Route path -> localized social copy. Used by pages and by legacy redirects. */
export const PAGE_SEO: Record<string, Record<Lang, PageMeta>> = {
  "/": {
    tr: {
      title: "PT Metin Özdemir | Personal Trainer & Movement Coach",
      description:
        "Metin Özdemir ile online kişisel antrenman, mobility, strength ve fonksiyonel dayanıklılık programları. Video eğitimler ve beslenme planları.",
      image: heroImage,
      imageAlt: "Metin Özdemir antrenman seansı",
    },
    en: {
      title: "PT Metin Ozdemir | Personal Trainer & Movement Coach",
      description:
        "Online personal training with Metin Ozdemir: mobility, strength and functional resilience programs, video lessons and nutrition plans.",
      image: heroImage,
      imageAlt: "Metin Ozdemir coaching a training session",
    },
  },
  "/programs": {
    tr: {
      title: "Egzersiz Programları | PT Metin Ozdemir",
      description:
        "Mobility, Strength in Range ve Functional Resilience programları. Seviyene uygun haftalık antrenman planlarıyla hareket kalitesini geliştir.",
      image: mobilityImage,
      imageAlt: "Antrenman programları",
    },
    en: {
      title: "Training Programs | PT Metin Ozdemir",
      description:
        "Mobility, Strength in Range and Functional Resilience programs. Weekly training plans built for your level to improve movement quality.",
      image: mobilityImage,
      imageAlt: "Training programs",
    },
  },
  "/premium": {
    tr: {
      title: "Premium Üyelik | PT Metin Ozdemir",
      description:
        "Starter, Premium ve Elite paketleriyle tüm programlara, video kütüphanesine ve birebir koçluğa erişim. Sana en uygun planı seç.",
      image: resilienceImage,
      imageAlt: "Premium üyelik paketleri",
    },
    en: {
      title: "Premium Membership | PT Metin Ozdemir",
      description:
        "Starter, Premium and Elite plans unlock every program, the full video library and one-to-one coaching. Pick the plan that fits you.",
      image: resilienceImage,
      imageAlt: "Premium membership plans",
    },
  },
  "/program/mobility": {
    tr: {
      title: "Mobility Programı | PT Metin Ozdemir",
      description:
        "Eklem hareket açıklığını ve hareket kalitesini geliştiren haftalık Mobility programı.",
      image: mobilityImage,
      imageAlt: "Mobility programı",
    },
    en: {
      title: "Mobility Program | PT Metin Ozdemir",
      description:
        "A weekly mobility program that improves joint range of motion and movement quality.",
      image: mobilityImage,
      imageAlt: "Mobility program",
    },
  },
  "/program/strength-in-range": {
    tr: {
      title: "Strength in Range Programı | PT Metin Ozdemir",
      description:
        "Hareket açıklığında kontrollü kuvvet geliştiren haftalık Strength in Range programı.",
      image: strengthImage,
      imageAlt: "Strength in Range programı",
    },
    en: {
      title: "Strength in Range Program | PT Metin Ozdemir",
      description:
        "Build controlled strength through full range of motion with a structured weekly plan.",
      image: strengthImage,
      imageAlt: "Strength in Range program",
    },
  },
  "/program/functional-resilience": {
    tr: {
      title: "Functional Resilience Programı | PT Metin Ozdemir",
      description:
        "Fonksiyonel dayanıklılık ve atletik performans için ileri seviye haftalık program.",
      image: resilienceImage,
      imageAlt: "Functional Resilience programı",
    },
    en: {
      title: "Functional Resilience Program | PT Metin Ozdemir",
      description:
        "An advanced program for durability and athletic performance, delivered as weekly training.",
      image: resilienceImage,
      imageAlt: "Functional Resilience program",
    },
  },
  "/dashboard": {
    tr: {
      title: "Üye Paneli | PT Metin Ozdemir",
      description: "Programlarını, ilerlemeni ve beslenme takibini tek panelden yönet.",
      image: dietImage,
      imageAlt: "Üye paneli",
    },
    en: {
      title: "Member Dashboard | PT Metin Ozdemir",
      description: "Manage your programs, progress and nutrition tracking in one place.",
      image: dietImage,
      imageAlt: "Member dashboard",
    },
  },
};

export const getPageMeta = (path: string, lang: string): PageMeta | undefined => {
  const entry = PAGE_SEO[path.split("?")[0]];
  return entry?.[normalizeLang(lang)];
};
